
import React from 'react';

interface ProjectionData {
  label: string;
  color: string;
  annualSavings: number;
  netCost: number;
}

interface LongTermProjectionProps {
  projections: ProjectionData[];
  monthlyBill: number;
}

export default function LongTermProjection({
  projections,
  monthlyBill,
}: LongTermProjectionProps) {
  if (!projections || projections.length === 0) return null;

  const annualBill = monthlyBill * 12;
  const rateIncreasePercent = 3.5;

  const getProjectedBillNoAction = (year: number) => {
    return Math.round(
      annualBill * Math.pow(1 + rateIncreasePercent / 100, year)
    );
  };

  const getCumulativeSavings = (annualSav: number, years: number) => {
    let total = 0;
    for (let y = 1; y <= years; y++) {
      const inflatedBill =
        annualBill * Math.pow(1 + rateIncreasePercent / 100, y);
      const savingsRatio = annualSav / annualBill;
      total += Math.round(inflatedBill * savingsRatio);
    }
    return total;
  };

  const getCumulativeBillNoAction = (years: number) => {
    let total = 0;
    for (let y = 1; y <= years; y++) {
      total += getProjectedBillNoAction(y);
    }
    return total;
  };

  const maxBar = getCumulativeBillNoAction(10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-red-light">
          <i className="ri-line-chart-line text-brand-red text-xl"></i>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">
            Long-Term Savings Projection
          </h3>
          <p className="text-xs text-gray-500">
            Includes {rateIncreasePercent}% annual utility rate increase (FL avg)
          </p>
        </div>
      </div>

      {/* Rate Increase Warning */}
      <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-100 flex-shrink-0">
            <i className="ri-arrow-up-double-line text-red-500 text-xl"></i>
          </div>
          <div>
            <h4 className="font-bold text-red-700 text-sm mb-1">
              Florida Rates Are Rising
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              FPL approved a 4-year rate hike starting Jan 2026. Florida
              electricity rates have increased approximately 3.5% annually over the past
              decade. Without action, your ${monthlyBill}/mo bill could become{' '}
              <strong>
                $
                {Math.round(
                  monthlyBill * Math.pow(1 + rateIncreasePercent / 100, 10)
                )}
                /mo in 10 years
              </strong>
              .
            </p>
          </div>
        </div>
      </div>

      {/* 5-Year / 10-Year Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 px-3 text-xs font-bold text-gray-500 uppercase">
                Scenario
              </th>
              <th className="text-right py-3 px-3 text-xs font-bold text-gray-500 uppercase">
                Year 1
              </th>
              <th className="text-right py-3 px-3 text-xs font-bold text-gray-500 uppercase">
                5-Year Total
              </th>
              <th className="text-right py-3 px-3 text-xs font-bold text-gray-500 uppercase">
                10-Year Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* No-action row */}
            <tr className="border-b border-gray-100 bg-red-50/50">
              <td className="py-3 px-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0"></div>
                  <span className="font-semibold text-gray-700 text-xs">
                    Do Nothing (Bills Paid)
                  </span>
                </div>
              </td>
              <td className="text-right py-3 px-3 font-bold text-red-600 text-xs">
                ${getProjectedBillNoAction(1).toLocaleString()}
              </td>
              <td className="text-right py-3 px-3 font-bold text-red-600 text-xs">
                ${getCumulativeBillNoAction(5).toLocaleString()}
              </td>
              <td className="text-right py-3 px-3 font-bold text-red-600 text-xs">
                ${getCumulativeBillNoAction(10).toLocaleString()}
              </td>
            </tr>

            {/* Projection rows */}
            {projections.map((p, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: p.color }}
                    ></div>
                    <span className="font-semibold text-gray-700 text-xs">
                      {p.label}
                    </span>
                  </div>
                </td>
                <td className="text-right py-3 px-3 font-bold text-green-600 text-xs">
                  ${p.annualSavings.toLocaleString()}
                </td>
                <td className="text-right py-3 px-3 font-bold text-green-600 text-xs">
                  ${getCumulativeSavings(p.annualSavings, 5).toLocaleString()}
                </td>
                <td className="text-right py-3 px-3 font-bold text-green-600 text-xs">
                  ${getCumulativeSavings(p.annualSavings, 10).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Visual Bar Chart */}
      <div className="space-y-4">
        <h4 className="text-sm font-bold text-gray-700">
          10-Year Cumulative Comparison
        </h4>

        {/* Do Nothing bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">
              Do Nothing (Total Bills Paid)
            </span>
            <span className="text-xs font-bold text-red-600">
              ${getCumulativeBillNoAction(10).toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
            <div
              className="h-full bg-red-400 rounded-full transition-all duration-1000"
              style={{ width: '100%' }}
            ></div>
          </div>
        </div>

        {/* Savings bars */}
        {projections.map((p, idx) => {
          const tenYearSavings = getCumulativeSavings(p.annualSavings, 10);
          const barWidth = Math.max(5, Math.round((tenYearSavings / maxBar) * 100));
          return (
            <div key={idx} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  {p.label} (Total Saved)
                </span>
                <span className="text-xs font-bold text-green-600">
                  ${tenYearSavings.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: p.color,
                  }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Net Worth Impact */}
      {projections.length > 0 && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-100">
              <i className="ri-money-dollar-box-line text-green-600 text-xl"></i>
            </div>
            <h4 className="font-bold text-gray-900 text-sm">
              Your 10-Year Financial Impact
            </h4>
          </div>

          {projections.map((p, idx) => {
            const tenYearSavings = getCumulativeSavings(p.annualSavings, 10);
            const netGain = tenYearSavings - p.netCost;
            const gainClass = netGain > 0 ? 'text-green-600' : 'text-red-500';
            const sign = netGain > 0 ? '+' : '';

            return (
              <div
                key={idx}
                className="flex items-center justify-between py-2 border-b border-green-200/50 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: p.color }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700">
                    {p.label}
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${gainClass}`}>
                    {sign}${netGain.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-gray-500 ml-1">
                    net gain
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
